import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import useAPI from '../../../api';
import { Grid } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import FormTextField from '../../../Components/Form/FormTextField';
import FormSelect from '../../../Components/Form/FormSelect';
import FormRadioStack from '../../../Components/Form/FormRadioStack';

const LongChannelFeaturesPage = function LongChannelFeaturesPage({
  errors,
  data,
  formData,
}) {
  const intl = useIntl();
  const API = useAPI();
  const { reset, resetField, watch } = useFormContext();
  const watcher = watch('features.hasNationalAgreement');
  const [segmentOptions, setSegmentOptions] = useState([]);
  const [dealerStateOptions, setDealerStateOptions] = useState([]);
  const [dealerRatingOptions, setDealerRatingOptions] = useState([]);

  const getDealerStateOptions = () => {
    API.getDynamicFeaturesOptionsById(
      52, //dealer state id
      { silent: true },
      (res) => {
        setDealerStateOptions(res);
      },
      () => {}
    );
  };

  const getSegmentOptions = () => {
    API.getDynamicFeaturesOptionsById(
      53, //segment id
      { silent: true },
      (res) => {
        setSegmentOptions(res);
      },
      () => {}
    );
  };

  const getDealerRatingOptions = () => {
    API.getDynamicFeaturesOptionsById(
      54, //dealer rating id
      { silent: true },
      (res) => {
        setDealerRatingOptions(res);
      },
      () => {}
    );
  };

  const hasNationalAgreementDefaultValue = (value) => {
    if (value)
      return value === intl.messages.YES || value === true
        ? intl.messages.YES
        : intl.messages.NO;
    return intl.messages.NO;
  };

  useEffect(() => {
    if (typeof data !== 'boolean' && Object.keys(data || {}).length > 0) {
      reset({
        ...(data ?? {}),
        ...(formData ?? {}),
      });
    }
  }, [data]);

  useEffect(() => {
    if (watcher === intl.messages.NO) {
      resetField('features.nationalAgreement', { defaultValue: '' });
    }
  }, [watcher]);

  useEffect(() => {
    getDealerStateOptions();
    getSegmentOptions();
    getDealerRatingOptions();
  }, []);

  return (
    <Grid container spacing={2}>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.profitability"
          label={intl.messages.PROFITABILITY}
          error={errors?.features?.profitability}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.profitabilityYTD"
          label={intl.messages.PROFITABILITYYTD}
          error={errors?.features?.profitabilityYTD}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.startingProfitability"
          label={intl.messages.STARTINGPROFITABILITY}
          error={errors?.features?.startingProfitability}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.litigation"
          label={intl.messages.LITIGATION}
          error={errors?.features?.litigation}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.overdraft"
          label={intl.messages.OVERDRAFT}
          error={errors?.features?.overdraft}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.risk"
          label={intl.messages.RISK}
          error={errors?.features?.risk}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.disbursed"
          label={intl.messages.DISBURSED}
          error={errors?.features?.disbursed}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.disbursedYTD"
          label={intl.messages.DISBURSEDYTD}
          error={errors?.features?.disbursedYTD}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.ytd"
          label={intl.messages.YTD}
          error={errors?.features?.ytd}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.ytd2"
          label={intl.messages.YTD2}
          error={errors?.features?.ytd2}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.cpi"
          label={intl.messages.CPI}
          error={errors?.features?.cpi}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          name="features.cvt"
          label={intl.messages.CVT}
          error={errors?.features?.cvt}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormSelect
          name="features.dealerRating"
          label={intl.messages.DEALERRATING}
          error={errors?.features?.dealerRating}
          options={dealerRatingOptions.map(({ value, displayValue }) => ({
            value,
            label: displayValue,
          }))}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormSelect
          name="features.dealerState"
          label={intl.messages.DEALERSTATE}
          error={errors?.features?.dealerState}
          options={dealerStateOptions.map(({ value, displayValue }) => ({
            value,
            label: displayValue,
          }))}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormSelect
          name="features.segment"
          label={intl.messages.SEGMENT}
          error={errors?.features?.segment}
          options={segmentOptions.map(({ value, displayValue }) => ({
            value,
            label: displayValue,
          }))}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          allUpperCase
          label={intl.messages.BUSINESSPACKAGE}
          name="features.businessPackage"
          error={errors?.features?.businessPackage}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormRadioStack
          label={intl.messages.HASNATIONALAGREEMENT}
          name="features.hasNationalAgreement"
          options={[intl.messages.YES, intl.messages.NO]}
          row
          required
          error={errors?.features?.hasNationalAgreement}
          defaultValue={hasNationalAgreementDefaultValue(
            formData?.features?.hasNationalAgreement ??
              data?.features?.hasNationalAgreement
          )}
        />
      </Grid>
      <Grid size={{ md: 4 }}>
        <FormTextField
          label={intl.messages.NATIONALAGREEMENT}
          name="features.nationalAgreement"
          error={errors?.features?.nationalAgreement}
          disabled={watcher !== intl.messages.YES}
        />
      </Grid>
    </Grid>
  );
};

LongChannelFeaturesPage.propTypes = {
  errors: PropTypes.object,
  data: PropTypes.object,
  formData: PropTypes.object,
};

export default LongChannelFeaturesPage;
